import React, { useState, useEffect } from 'react';
import { X, Save, Trash2, Mail, User, Briefcase, Calendar, UploadCloud, FileText, ExternalLink } from 'lucide-react';
import { doc, updateDoc, deleteDoc, addDoc, collection } from 'firebase/firestore';
import { db } from '../../firebase/config';
import useStorage from '../../hooks/useStorage'; // Import the new hook

const ApplicantDetailModal = ({ isOpen, onClose, applicant, currentUserProfile, availableRoles = [] }) => {
    const initialFormState = {
        name: '',
        email: '',
        role: '',
        status: 'Applied',
        notes: '',
        resumeUrl: '',   // Link to download
        resumeName: '',  // "john_resume.pdf"
        resumePath: ''   // "organizations/123/resumes/..." (needed for deletion)
    };

    const [formData, setFormData] = useState(initialFormState);
    const [isSaving, setIsSaving] = useState(false);
    
    // Use our new hook
    const { uploadFile, deleteFile, progress, isUploading } = useStorage();

    useEffect(() => {
        if (applicant && applicant.id) {
            setFormData({
                name: applicant.name || '',
                email: applicant.email || '',
                role: applicant.role || '',
                status: applicant.status || 'Applied',
                notes: applicant.notes || '',
                resumeUrl: applicant.resumeUrl || '',
                resumeName: applicant.resumeName || '',
                resumePath: applicant.resumePath || ''
            });
        } else {
            setFormData({
                ...initialFormState,
                role: availableRoles.length > 0 ? availableRoles[0] : ''
            });
        }
    }, [applicant, isOpen, availableRoles]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // --- 1. HANDLE FILE SELECTION ---
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Create a unique path: orgId/resumes/timestamp_filename
        const storagePath = `organizations/${currentUserProfile.organizationId}/resumes/${Date.now()}_${file.name}`;
        
        try {
            const downloadUrl = await uploadFile(file, storagePath);
            // Update form state with the new file info
            setFormData(prev => ({
                ...prev,
                resumeUrl: downloadUrl,
                resumeName: file.name,
                resumePath: storagePath
            }));
        } catch (error) {
            alert("Failed to upload resume.");
        }
    };

    // --- 2. HANDLE FILE REMOVAL ---
    const handleRemoveResume = async () => {
        if (window.confirm("Remove this resume attachment?")) {
            // Delete from storage bucket
            if (formData.resumePath) {
                await deleteFile(formData.resumePath);
            }
            // Clear from form state
            setFormData(prev => ({
                ...prev,
                resumeUrl: '',
                resumeName: '',
                resumePath: ''
            }));
        }
    };

    const handleSave = async () => {
        if (!formData.name) return alert("Name is required");
        setIsSaving(true);
        
        try {
            if (applicant && applicant.id) {
                const docRef = doc(db, `organizations/${currentUserProfile.organizationId}/applicants`, applicant.id);
                await updateDoc(docRef, { ...formData, lastUpdated: new Date() });
            } else {
                const collectionRef = collection(db, `organizations/${currentUserProfile.organizationId}/applicants`);
                await addDoc(collectionRef, { ...formData, createdAt: new Date(), lastUpdated: new Date() });
            }
            onClose();
        } catch (error) {
            console.error("Error saving applicant:", error);
            alert("Failed to save changes.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!applicant || !applicant.id) return;
        if (window.confirm("Are you sure you want to remove this applicant permanently?")) {
            try {
                // CLEANUP: Delete the resume file from storage if it exists
                if (applicant.resumePath) {
                    await deleteFile(applicant.resumePath);
                }
                // Delete the database record
                await deleteDoc(doc(db, `organizations/${currentUserProfile.organizationId}/applicants`, applicant.id));
                onClose();
            } catch (error) {
                console.error("Error deleting applicant:", error);
            }
        }
    };

    const isEditing = applicant && applicant.id;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col max-h-[90vh]">
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="text-xl font-bold flex items-center gap-2">
                        <User size={20} className="text-blue-600"/> 
                        {isEditing ? 'Edit Applicant' : 'New Applicant'}
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X size={24} /></button>
                </div>

                <div className="p-6 overflow-y-auto space-y-4">
                    {/* Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                        <div className="relative">
                            <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full pl-10 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none" />
                        </div>
                    </div>

                    {/* Role */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Role Applied For</label>
                        <div className="relative">
                            <Briefcase size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            {availableRoles.length > 0 ? (
                                <select name="role" value={formData.role} onChange={handleChange} className="w-full pl-10 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-white">
                                    <option value="" disabled>Select a role...</option>
                                    {availableRoles.map(role => (<option key={role} value={role}>{role}</option>))}
                                </select>
                            ) : (
                                <input type="text" name="role" value={formData.role} onChange={handleChange} placeholder="Type role manually" className="w-full pl-10 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none" />
                            )}
                        </div>
                    </div>

                    {/* Email */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                        <div className="relative">
                            <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full pl-10 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none" />
                        </div>
                    </div>

                    {/* Status */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Current Status</label>
                        <select name="status" value={formData.status} onChange={handleChange} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-white">
                            <option value="Applied">Applied</option>
                            <option value="Interviewing">Interviewing</option>
                            <option value="Offer Sent">Offer Sent</option>
                            <option value="Hired">Hired</option>
                            <option value="Rejected">Rejected</option>
                        </select>
                    </div>

                    {/* RESUME UPLOAD AREA */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Resume / Attachment</label>
                        
                        {/* A. Loading State */}
                        {isUploading && (
                            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2 mt-2">
                                <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
                                <p className="text-xs text-center text-gray-500 mt-1">Uploading... {Math.round(progress)}%</p>
                            </div>
                        )}

                        {/* B. File Exists State */}
                        {!isUploading && formData.resumeUrl ? (
                            <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-md">
                                <div className="flex items-center gap-2 overflow-hidden">
                                    <FileText size={20} className="text-blue-500 flex-shrink-0" />
                                    <div className="flex flex-col">
                                        <a href={formData.resumeUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-700 hover:underline font-medium truncate max-w-[150px]">
                                            {formData.resumeName || "Resume.pdf"}
                                        </a>
                                        <span className="text-xs text-gray-400">Click to view</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <a href={formData.resumeUrl} target="_blank" rel="noopener noreferrer" className="p-2 text-blue-600 hover:bg-blue-100 rounded" title="Open File">
                                        <ExternalLink size={16} />
                                    </a>
                                    <button onClick={handleRemoveResume} className="p-2 text-red-500 hover:bg-red-100 rounded" title="Remove File">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            /* C. Empty State (Upload Box) */
                            !isUploading && (
                                <div className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center hover:bg-gray-50 transition-colors relative group">
                                    <input 
                                        type="file" 
                                        onChange={handleFileChange}
                                        accept=".pdf,.doc,.docx,.txt"
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                    />
                                    <UploadCloud className="mx-auto h-8 w-8 text-gray-400 group-hover:text-blue-500 transition-colors mb-2" />
                                    <p className="text-sm text-gray-600 font-medium">Click to upload resume</p>
                                    <p className="text-xs text-gray-400 mt-1">PDF, Word, or Text (Max 5MB)</p>
                                </div>
                            )
                        )}
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Interview Notes</label>
                        <textarea name="notes" rows="4" value={formData.notes} onChange={handleChange} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none"></textarea>
                    </div>

                    {isEditing && (
                        <div className="text-xs text-gray-400 flex items-center gap-1 mt-2">
                            <Calendar size={12} /> Applied: {applicant.createdAt?.toDate?.().toLocaleDateString() || 'Unknown'}
                        </div>
                    )}
                </div>

                <div className="p-4 border-t bg-gray-50 flex justify-between items-center rounded-b-lg">
                    {isEditing ? (
                        <button onClick={handleDelete} className="text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-50"><Trash2 size={20} /></button>
                    ) : <div></div>}
                    <div className="flex gap-3">
                        <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-md">Cancel</button>
                        <button onClick={handleSave} disabled={isSaving || isUploading} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"><Save size={18} />{isSaving ? 'Saving...' : 'Save Changes'}</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApplicantDetailModal;